import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  RadioGroup,
  Radio,
  Select,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material';
import colors from '../../../config/colors';
import { IntegraFormData, integraFormSchema } from './schema';
import {
  useRegionesQuery,
  useProvinciasQuery,
  useMunicipiosQuery,
  useProfesionalesDiagnosticoQuery,
  useEvaluacionesAplicadasQuery,
  useComorbilidadesQuery,
  useTerapiasQuery,
  useCondicionesSocialesQuery,
  useDependientesElegiblesQuery,
  useRelacionParentescosQuery,
  DependienteElegible,
} from '../../state/queries/collections';
import { FormSection } from './integra-form-section';
import { Step2DocumentUpload } from './integra-step2';
import useSetTitlePage from '../../hooks/useSetTitlePage';
import CustomLabel from '../../components/common/customLabel';
import { labelDatosPersonalesProfile } from '../../global/styles';
import { useAfiliadoProfileQuery } from '../../state/queries/afiliado';

export function ProgramaIntegraPage() {
  const { setTitle } = useSetTitlePage();
  setTitle(undefined);
  const infoAfiliado = useSelector((state: any) => state.afiliado.infoAfiliado);
  const afiliadoProfileQuery = useAfiliadoProfileQuery(infoAfiliado?.codigoAfiliado);

  if (afiliadoProfileQuery.isLoading) {
    return <LinearProgress />;
  }

  if (afiliadoProfileQuery.isError) {
    // return some component
  }
  return <ProgramaIntegraPageReady />;
}

function ProgramaIntegraPageReady() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const infoAfiliado = useSelector((state: any) => state.afiliado.infoAfiliado);
  const afiliadoProfileQuery = useAfiliadoProfileQuery(infoAfiliado?.codigoAfiliado);

  const documentoIdentidad: string = afiliadoProfileQuery.data?.result?.datosPersonales.documentoIdentidad;
  const datosContactoTutor = afiliadoProfileQuery.data?.result?.datosContacto;

  useEffect(() => {
    if (localStorage.getItem('autenticado') == 'false') navigate('../../login', { replace: true });
  }, []);

  console.log('INFO AFILIADO: ', {
    infoAfiliado,
    datosPersonales: afiliadoProfileQuery.data?.result?.datosPersonales,
    datosContactoTutor,
  });

  const esTitular = infoAfiliado?.tipoAfiliacion?.toLowerCase() === 'titular';

  useEffect(() => {
    if (infoAfiliado?.codigoAfiliado && !esTitular) {
      navigate('/', { replace: true });
    }
  }, [esTitular, infoAfiliado?.codigoAfiliado, navigate]);

  // ─── Step state ────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [step1FormData, setStep1FormData] = useState<IntegraFormData | null>(null);

  // ─── Beneficiary selection ─────────────────────────────────────────────────
  const [selectedDependienteId, setSelectedDependienteId] = useState<number | ''>('');

  const {
    data: dependientes,
    isLoading: isLoadingDependientes,
    isError: isErrorDependientes,
  } = useDependientesElegiblesQuery(infoAfiliado?.codigoAfiliado ?? null);

  const dependienteSeleccionado = useMemo<DependienteElegible | undefined>(
    () => dependientes?.find((d) => d.idDependiente === selectedDependienteId),
    [dependientes, selectedDependienteId]
  );

  const dependienteSeleccionadoQuery = useAfiliadoProfileQuery(dependienteSeleccionado?.idDependiente ?? null);

  const hayElegibles = useMemo(() => dependientes?.some((d) => d.esElegible) ?? false, [dependientes]);

  // ─── Catalogs ──────────────────────────────────────────────────────────────
  const { data: provincias } = useProvinciasQuery();
  const { data: municipios } = useMunicipiosQuery();
  const { data: profesionales } = useProfesionalesDiagnosticoQuery();
  const { data: evaluaciones } = useEvaluacionesAplicadasQuery();
  const { data: comorbilidades } = useComorbilidadesQuery();
  const { data: terapias } = useTerapiasQuery();
  const { data: condicionesSociales } = useCondicionesSocialesQuery();
  const { data: parentescos } = useRelacionParentescosQuery();

  // ─── Form ──────────────────────────────────────────────────────────────────
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {},
  } = useForm<IntegraFormData>({
    resolver: joiResolver(integraFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      codigoProvincia: '',
      codigoMunicipio: '',
      direccion: '',
      nss: '',
      fechaNacimiento: '',
      edad: '',
      parentesco: '',
      telefono: datosContactoTutor?.telefonoResidencial ?? '',
      celular: datosContactoTutor?.telefonoCelular ?? '',
      correoElectronico: datosContactoTutor?.correoElectronico ?? '',
      correoElectronicoAlterno: '',
      nivelEducativo: '',
      situacionLaboral: '',
      estadoCivil: '',
      tieneTeaDiagnosticado: '',
      fechaDiagnostico: '',
      idProfesionalDiagnostico: '',
      tieneOtroDiagnosticoProfesional: false,
      otroProfesionalDiagnostico: '',
      recibeTerapias: '',
      centroTerapia: '',
      recibeTerapiaIdList: [],
      tieneOtraTerapia: false,
      otraTerapia: '',
      evaluacionesAplicadasIdList: [],
      tieneOtraEvaluacion: false,
      otraEvaluacionAplicada: '',
      comorbilidadIdList: [],
      tieneOtraComorbilidad: false,
      otraComorbilidad: '',
      idCondicionSocioeconomica: '',
      otraSituacion: '',
      recibeSubsidio: '',
      tipoSubsidio: '',
      consentimiento: '',
    },
  });

  const tieneTeaDiagnosticado = watch('tieneTeaDiagnosticado');
  const recibeTerapias = watch('recibeTerapias');
  const tieneOtroDiagnosticoProfesional = watch('tieneOtroDiagnosticoProfesional');
  const tieneOtraEvaluacion = watch('tieneOtraEvaluacion');
  const tieneOtraComorbilidad = watch('tieneOtraComorbilidad');
  const tieneOtraTerapia = watch('tieneOtraTerapia');
  const idCondicionSocioeconomica = watch('idCondicionSocioeconomica');
  const recibeSubsidio = watch('recibeSubsidio');
  const provincia = watch('codigoProvincia');

  console.log({ provincia });

  // Sync beneficiary read-only data into form state when queries resolve
  useEffect(() => {
    const dp = dependienteSeleccionadoQuery.data?.result?.datosPersonales;
    setValue('nss', dp?.nss ?? '');
    setValue('fechaNacimiento', dp?.fechaNacimiento ?? '');
    setValue('edad', dependienteSeleccionado ? String(dependienteSeleccionado.edad) : '');
  }, [dependienteSeleccionadoQuery.data, dependienteSeleccionado, setValue]);

  const showTeaDetails = tieneTeaDiagnosticado === 'si';
  const showTerapiasSection = showTeaDetails && recibeTerapias === 'si';
  const condicionSeleccionada = condicionesSociales?.find((c) => String(c.id) === idCondicionSocioeconomica);
  const showOtraSituacion = condicionSeleccionada?.nombre === 'Otro';

  // Step 1: validate form + dependiente, then advance to step 2 (no API call)
  const onSubmit = (data: IntegraFormData) => {
    if (!dependienteSeleccionado) {
      enqueueSnackbar('Debe seleccionar un dependiente elegible antes de continuar', { variant: 'warning' });
      return;
    }
    setStep1FormData(data);
    setCurrentStep(2);
  };

  const onError = () => {
    enqueueSnackbar('Existen campos requeridos sin completar, favor indicarlos antes de continuar', {
      variant: 'error',
    });
  };

  // ─── Step 2 ────────────────────────────────────────────────────────────────
  if (currentStep === 2 && step1FormData && dependienteSeleccionado) {
    return (
      <Step2DocumentUpload
        step1Data={step1FormData}
        parentescosOptions={parentescos ?? []}
        infoAfiliado={infoAfiliado}
        dependienteSeleccionado={dependienteSeleccionado}
        documentoIdentidad={documentoIdentidad ?? ''}
        onBack={() => setCurrentStep(1)}
      />
    );
  }

  // ─── Step 1 ────────────────────────────────────────────────────────────────
  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit, onError)}
      sx={{ maxWidth: 1200, mx: 'auto', py: 3, px: { xs: 2, md: 3 } }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant='h5' sx={{ fontWeight: 'bold', color: colors.primary, mb: 0.5 }}>
          Formulario de Solicitud - Programa InTEgrA
        </Typography>
        <Typography variant='body2' sx={{ fontFamily: 'Montserrat', color: colors.gray }}>
          Paso 1 de 2 — Información del Beneficiario
        </Typography>
      </Box>

      {/* ── Selección de Beneficiario ── */}
      <Paper
        elevation={0}
        sx={{ mb: 3, border: `1px solid ${colors.grayLight}`, borderRadius: 2, overflow: 'hidden', p: 3 }}
      >
        <Typography variant='h6' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', color: colors.primary, mb: 2 }}>
          Selección de Beneficiario
        </Typography>
        <Typography variant='body2' sx={{ fontFamily: 'Montserrat', color: colors.gray, mb: 2 }}>
          Seleccione el dependiente al que desea aplicarle la solicitud del programa.
        </Typography>

        {isLoadingDependientes && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant='body2' sx={{ fontFamily: 'Montserrat' }}>
              Consultando elegibilidad de dependientes...
            </Typography>
          </Box>
        )}

        {isErrorDependientes && (
          <Alert severity='error' sx={{ mb: 2 }}>
            Ocurrió un error al consultar la elegibilidad de los dependientes. Intente nuevamente más tarde.
          </Alert>
        )}

        {!isLoadingDependientes && dependientes && dependientes.length === 0 && (
          <Alert severity='info'>No se encontraron dependientes registrados para este afiliado.</Alert>
        )}

        {!isLoadingDependientes && dependientes && dependientes.length > 0 && (
          <>
            {!hayElegibles && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                Ninguno de sus dependientes es elegible para este programa en este momento.
              </Alert>
            )}
            <FormControl fullWidth size='small'>
              <InputLabel>Dependiente *</InputLabel>
              <Select
                value={selectedDependienteId}
                label='Dependiente *'
                onChange={(e) => setSelectedDependienteId(e.target.value as number)}
                renderValue={(value) => {
                  const dep = dependientes.find((d) => d.idDependiente === value);
                  return dep ? dep.nombreCompleto : '';
                }}
              >
                {dependientes.map((dep) => (
                  <MenuItem
                    key={dep.idDependiente}
                    value={dep.idDependiente}
                    disabled={!dep.esElegible}
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <ListItemText primary={dep.nombreCompleto} secondary={`${dep.edad} años`} />
                      <Chip
                        label={dep.esElegible ? 'Elegible' : 'No elegible'}
                        size='small'
                        color={dep.esElegible ? 'success' : 'default'}
                        variant={dep.esElegible ? 'filled' : 'outlined'}
                      />
                    </Box>
                    {!dep.esElegible && dep.restricciones.length > 0 && (
                      <Box sx={{ mt: 0.5, pl: 1 }}>
                        {dep.restricciones.map((r, idx) => (
                          <Typography
                            key={idx}
                            variant='caption'
                            sx={{ display: 'block', color: 'error.main', fontFamily: 'Montserrat', lineHeight: 1.6 }}
                          >
                            • {r}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Paper>

      {/* ── Sección 1: Datos Generales del Beneficiario ── */}
      <FormSection
        number={1}
        title='Datos Generales del Beneficiario'
        description='Información personal del beneficiario prellenada por el sistema.'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label='Nombre Completo'
              value={dependienteSeleccionado?.nombreCompleto ?? ''}
              disabled
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label='Número de seguro social (NSS)'
              value={dependienteSeleccionado ? dependienteSeleccionadoQuery.data?.result.datosPersonales.nss : ''}
              disabled
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label='Fecha de nacimiento'
              value={
                dependienteSeleccionado ? dependienteSeleccionadoQuery.data?.result.datosPersonales.fechaNacimiento : ''
              }
              disabled
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label='Edad'
              value={dependienteSeleccionado ? `${dependienteSeleccionado.edad} años` : ''}
              disabled
              fullWidth
              size='small'
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* <Grid item xs={12} sm={4}>
            <Controller
              name='codigoRegion'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth size='small' error={!!fieldState.error}>
                  <InputLabel>Región</InputLabel>
                  <Select {...field} label='Región'>
                    {regiones?.result?.map((r) => (
                      <MenuItem key={r.codigo} value={String(r.codigo)}>
                        {r.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid> */}
          <Grid item xs={12} sm={4}>
            <Controller
              name='codigoProvincia'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth size='small' error={!!fieldState.error}>
                  <InputLabel>Provincia</InputLabel>
                  <Select {...field} label='Provincia'>
                    {provincias?.result?.map((p) => (
                      <MenuItem key={p.codigo} value={String(p.codigo)}>
                        {p.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name='codigoMunicipio'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth size='small' error={!!fieldState.error} disabled={provincia === ''}>
                  <InputLabel>Municipio</InputLabel>
                  <Select {...field} label='Municipio'>
                    {municipios?.result
                      ?.filter((m) => m.codigoPadre === Number(provincia))
                      .map((m) => (
                        <MenuItem key={m.codigo} value={String(m.codigo)}>
                          {m.descripcion}
                        </MenuItem>
                      ))}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='direccion'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Dirección de Residencia'
                  fullWidth
                  size='small'
                  multiline
                  rows={2}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* ── Sección 2: Datos del Padre / Madre / Tutor Legal ── */}
      <FormSection
        number={2}
        title='Datos del Padre / Madre / Tutor Legal'
        description='Información de contacto y datos del representante legal del beneficiario.'
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label='Nombres' value={infoAfiliado?.nombres ?? ''} disabled fullWidth size='small' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label='Apellidos' value={infoAfiliado?.apellidos ?? ''} disabled fullWidth size='small' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Documento de Identidad'
              value={documentoIdentidad ?? ''}
              defaultValue={undefined}
              disabled
              fullWidth
              size='small'
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name='parentesco'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth size='small' error={!!fieldState.error}>
                  <InputLabel>Parentesco *</InputLabel>
                  <Select {...field} label='Parentesco *'>
                    {parentescos?.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='estadoCivil'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Estado Civil'
                  placeholder='SOLTERO'
                  fullWidth
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='celular'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Celular'
                  placeholder='809-555-1234'
                  fullWidth
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'Requerido si no indica teléfono'}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='telefono'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Teléfono'
                  placeholder='809-555-1234'
                  fullWidth
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'Requerido si no indica celular'}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='correoElectronico'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Correo Electrónico'
                  fullWidth
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='correoElectronicoAlterno'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label='Correo Electrónico Alterno (Opcional)'
                  fullWidth
                  size='small'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='nivelEducativo'
              control={control}
              render={({ field }) => <TextField {...field} label='Nivel Educativo (Opcional)' fullWidth size='small' />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='situacionLaboral'
              control={control}
              render={({ field }) => (
                <TextField {...field} label='Situación Laboral (Opcional)' fullWidth size='small' />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* ── Sección 3: Información Clínica ── */}
      <FormSection
        number={3}
        title='Información Clínica'
        description='Historial clínico relacionado con el Trastorno del Espectro Autista (TEA).'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='tieneTeaDiagnosticado'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error} component='fieldset'>
                  <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 0.5 }}>
                    ¿Ha sido diagnosticado con TEA? *
                  </Typography>
                  <RadioGroup {...field} row>
                    <FormControlLabel value='si' control={<Radio size='small' />} label='Sí' />
                    <FormControlLabel value='no' control={<Radio size='small' />} label='No' />
                  </RadioGroup>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {showTeaDetails && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name='fechaDiagnostico'
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type='date'
                      label='Fecha de Diagnóstico *'
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size='small'
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              {/* Profesional que diagnostica */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 1 }}>
                  Profesional que diagnostica *
                </Typography>
                <Controller
                  name='idProfesionalDiagnostico'
                  control={control}
                  render={({ field }) => {
                    const radioValue = tieneOtroDiagnosticoProfesional
                      ? 'otro'
                      : field.value !== ''
                        ? String(field.value)
                        : '';
                    return (
                      <RadioGroup
                        value={radioValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'otro') {
                            field.onChange('');
                            setValue('tieneOtroDiagnosticoProfesional', true);
                            setValue('otroProfesionalDiagnostico', '');
                          } else {
                            field.onChange(Number(val));
                            setValue('tieneOtroDiagnosticoProfesional', false);
                            setValue('otroProfesionalDiagnostico', '');
                          }
                        }}
                      >
                        {profesionales?.map((item) => (
                          <FormControlLabel
                            key={item.id}
                            value={String(item.id)}
                            control={<Radio size='small' />}
                            label={item.nombre}
                          />
                        ))}
                        <FormControlLabel value='otro' control={<Radio size='small' />} label='Otro' />
                      </RadioGroup>
                    );
                  }}
                />
                {tieneOtroDiagnosticoProfesional && (
                  <>
                    <Controller
                      name='otroProfesionalDiagnostico'
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label='Especifique el profesional *'
                          fullWidth
                          size='small'
                          sx={{ mt: 1 }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    <Alert severity='info' sx={{ mt: 1.5, fontFamily: 'Montserrat', fontSize: '0.8rem' }}>
                      Si el profesional indicado no pertenece a nuestra red regulada, su solicitud seguirá siendo
                      evaluada. Nuestro equipo le recomendará un profesional autorizado durante el proceso para que
                      pueda continuar con los trámites.
                    </Alert>
                  </>
                )}
              </Grid>

              {/* ¿Recibe terapias? */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Controller
                  name='recibeTerapias'
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl error={!!fieldState.error} component='fieldset'>
                      <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 0.5 }}>
                        ¿Recibe terapias? *
                      </Typography>
                      <RadioGroup {...field} row>
                        <FormControlLabel value='si' control={<Radio size='small' />} label='Sí' />
                        <FormControlLabel value='no' control={<Radio size='small' />} label='No' />
                      </RadioGroup>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {recibeTerapias === 'si' && (
                <Grid item xs={12}>
                  <Controller
                    name='centroTerapia'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label='Centro donde recibe terapias *'
                        fullWidth
                        size='small'
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Evaluaciones Aplicadas */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 1 }}>
                  Evaluaciones Aplicadas
                </Typography>
                <Controller
                  name='evaluacionesAplicadasIdList'
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {evaluaciones?.map((item) => (
                        <FormControlLabel
                          key={item.id}
                          control={
                            <Checkbox
                              size='small'
                              checked={field.value.includes(item.id)}
                              onChange={(e) => {
                                const updated = e.target.checked
                                  ? [...field.value, item.id]
                                  : field.value.filter((id) => id !== item.id);
                                field.onChange(updated);
                              }}
                            />
                          }
                          label={item.nombre}
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                <Controller
                  name='tieneOtraEvaluacion'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={field.onChange} size='small' />}
                      label='Otro'
                    />
                  )}
                />
                {tieneOtraEvaluacion && (
                  <Controller
                    name='otraEvaluacionAplicada'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label='Especifique la evaluación *'
                        fullWidth
                        size='small'
                        sx={{ mt: 1 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      </FormSection>

      {/* ── Sección 4: Comorbilidades Asociadas ── */}
      <FormSection
        number={4}
        title='Comorbilidades Asociadas'
        description='Condiciones médicas asociadas que presenta el beneficiario.'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='comorbilidadIdList'
              control={control}
              render={({ field }) => (
                <FormGroup row>
                  {comorbilidades?.map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          size='small'
                          checked={field.value.includes(item.id)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...field.value, item.id]
                              : field.value.filter((id) => id !== item.id);
                            field.onChange(updated);
                          }}
                        />
                      }
                      label={item.nombre}
                    />
                  ))}
                </FormGroup>
              )}
            />
            <Controller
              name='tieneOtraComorbilidad'
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={field.onChange} size='small' />}
                  label='Otro'
                />
              )}
            />
            {tieneOtraComorbilidad && (
              <Controller
                name='otraComorbilidad'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label='Especifique la comorbilidad *'
                    fullWidth
                    size='small'
                    sx={{ mt: 1 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          </Grid>
        </Grid>
      </FormSection>

      {/* ── Sección 5: Situación Terapéutica Actual (condicional) ── */}
      {showTerapiasSection && (
        <FormSection
          number={5}
          title='Situación Terapéutica Actual'
          description='Detalle de las terapias que recibe actualmente el beneficiario.'
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 1 }}>
                Tipo de Terapia
              </Typography>
              <Controller
                name='recibeTerapiaIdList'
                control={control}
                render={({ field }) => (
                  <FormGroup row>
                    {terapias?.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            size='small'
                            checked={field.value.includes(item.id)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...field.value, item.id]
                                : field.value.filter((id) => id !== item.id);
                              field.onChange(updated);
                            }}
                          />
                        }
                        label={item.nombre}
                      />
                    ))}
                  </FormGroup>
                )}
              />
              <Controller
                name='tieneOtraTerapia'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={field.onChange} size='small' />}
                    label='Otro'
                  />
                )}
              />
              {tieneOtraTerapia && (
                <Controller
                  name='otraTerapia'
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label='Especifique la terapia *'
                      fullWidth
                      size='small'
                      sx={{ mt: 1 }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </FormSection>
      )}

      {/* ── Sección 6: Situación Social y Familiar ── */}
      <FormSection
        number={6}
        title='Situación Social y Familiar'
        description='Contexto socioeconómico y familiar del beneficiario.'
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name='idCondicionSocioeconomica'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth size='small' error={!!fieldState.error}>
                  <InputLabel>Condición Socioeconómica *</InputLabel>
                  <Select {...field} label='Condición Socioeconómica *'>
                    {condicionesSociales?.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {showOtraSituacion && (
            <Grid item xs={12} sm={6}>
              <Controller
                name='otraSituacion'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label='Especifique la condición *'
                    fullWidth
                    size='small'
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name='recibeSubsidio'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error} component='fieldset'>
                  <Typography variant='body2' sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', mb: 0.5 }}>
                    ¿Recibe subsidios?
                  </Typography>
                  <RadioGroup {...field} row>
                    <FormControlLabel value='si' control={<Radio size='small' />} label='Sí' />
                    <FormControlLabel value='no' control={<Radio size='small' />} label='No' />
                  </RadioGroup>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {recibeSubsidio === 'si' && (
            <Grid item xs={12} sm={6}>
              <Controller
                name='tipoSubsidio'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label='Tipo de Subsidio *'
                    fullWidth
                    size='small'
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>

      {/* ── Sección 7: Consentimiento Informado ── */}
      <FormSection
        number={7}
        title='Consentimiento Informado'
        description='Autorización para el uso de información en el programa SeNaSa Integra.'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grayUltraLight, borderRadius: 2, mb: 2 }}>
              <Typography variant='body2' sx={{ fontFamily: 'Montserrat', lineHeight: 1.8 }}>
                Yo,{' '}
                <strong>
                  {infoAfiliado?.nombres} {infoAfiliado?.apellidos}
                </strong>
                , en calidad de representante legal de{' '}
                <strong>{dependienteSeleccionado?.nombreCompleto ?? '___'}</strong>, declaro que he sido debidamente
                informado(a) sobre el Programa SeNaSa Integra y autorizo el uso de la información clínica y
                administrativa para fines de atención, monitoreo y evaluación, conforme a la Ley 34-23 y la normativa
                vigente.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='consentimiento'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error} component='fieldset'>
                  <RadioGroup {...field}>
                    <FormControlLabel value='si' control={<Radio size='small' />} label='Sí, estoy de acuerdo' />
                    <FormControlLabel value='no' control={<Radio size='small' />} label='No, no acepto' />
                  </RadioGroup>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* ── Botones de Acción ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 4 }}>
        <Button
          variant='outlined'
          onClick={() => navigate(-1)}
          sx={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            px: 4,
            borderColor: colors.grayLight,
            color: colors.gray,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant='contained'
          type='submit'
          disabled={!dependienteSeleccionado}
          sx={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            px: 4,
            bgcolor: colors.primary,
            '&:hover': { bgcolor: colors.primaryDark },
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}
